import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  CreatePoSRegister,
  PoSIntegrationData,
  PoSIntegrationParams,
  AvailableRegistersQueryParams,
  AvailablePoSRegisters,
  GetPoSRegistersQueryParams,
  UpdatePoSIntegrationRequest,
  PoSRegisterResponse,
  UpdatePoSRegister,
  Location,
  ApiCameraWithIncludes,
} from '@eencloud/eewc-components/src/service/api-types';
import { useAccountStore } from './account';

import APIList from '@eencloud/eewc-components/src/service/api';

export const usePoSIntegrationsStore = defineStore('posIntegrations', () => {
  const accountStore = useAccountStore();

  const posIntegrations = ref<PoSIntegrationData[]>([]);
  const availablePoSRegisters = ref<AvailablePoSRegisters[]>([]);
  const availablePoSRegistersLoading = ref<boolean>(false);
  const posRegisters = ref<PoSRegisterResponse[]>([]);
  // sitesWithCameras is used for filtering the sites that has cameras length > 0
  const sitesWithCameras = ref<Location[]>([]);
  //  camerasWithLocationSummary is used for displaying the camera site without fetching the site details
  const camerasWithLocationSummary = ref<ApiCameraWithIncludes[]>([]);
  const posRegister = ref<PoSRegisterResponse | null>(null);

  const PAGE_SIZE = 100;

  const isPointOfSalePackageEnabled = computed(() => {
    return accountStore.account?.packages?.includes('een.pos');
  });

  const fetchPoSIntegrations = async (params: PoSIntegrationParams) => {
    try {
      let nextPageToken: string | undefined;
      let integrationsList: PoSIntegrationData[] = [];
      do {
        params.pageSize = PAGE_SIZE;
        if (nextPageToken) params.pageToken = nextPageToken;
        const response = await APIList.getPoSIntegrations(params);
        const data = response.data;
        nextPageToken = data.nextPageToken;
        integrationsList = [...integrationsList, ...data.results];
      } while (nextPageToken);

      posIntegrations.value = integrationsList;
    } catch (error) {
      console.error(error);
    }
  };

  const connectToPoSTerminal = async (integrationID: string, payload: UpdatePoSIntegrationRequest) => {
    try {
      const response = await APIList.updatePoSIntegration(integrationID, payload);
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 204) {
        return await getPoSIntegrationByID(integrationID);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getPoSIntegrationByID = async (integrationID: string) => {
    try {
      const response = await APIList.getPoSIntegration(integrationID);
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getAvailablePoSIntegrationRegisters = async (integrationID: string) => {
    try {
      const params: AvailableRegistersQueryParams = { pageSize: PAGE_SIZE };
      availablePoSRegistersLoading.value = true;
      let nextPageToken: string | undefined;
      let posRegisters: AvailablePoSRegisters[] = [];
      do {
        if (nextPageToken) params.pageToken = nextPageToken;
        const response = await APIList.getAvailablePoSRegisters(integrationID, params);
        if (response.status === 200) {
          const data = response.data;
          nextPageToken = data.nextPageToken;
          posRegisters = [...posRegisters, ...data.results];
        }
      } while (nextPageToken);
      availablePoSRegisters.value = posRegisters;
      return posRegisters;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      availablePoSRegistersLoading.value = false;
    }
  };

  const updatePoSIntegration = async (integrationID: string, payload: UpdatePoSIntegrationRequest) => {
    try {
      const response = await APIList.updatePoSIntegration(integrationID, payload);
      if (response.status === 204) {
        return response;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const addPosRegister = async (payload: CreatePoSRegister) => {
    try {
      const response = await APIList.createPoSRegister(payload);
      return response;
    } catch (error) {
      console.debug(error);
      return null;
    }
  };

  const getPoSRegisters = async (params: GetPoSRegistersQueryParams) => {
    try {
      params.pageSize = PAGE_SIZE;
      let nextPageToken: string | undefined;
      let registers: PoSRegisterResponse[] = [];
      do {
        if (nextPageToken) params.pageToken = nextPageToken;
        const response = await APIList.getPoSRegisters(params);
        if (response.status === 200) {
          registers = [...registers, ...response.data.results];
          nextPageToken = response.data.nextPageToken;
        }
      } while (nextPageToken);
      posRegisters.value = registers;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const updatePosRegister = async (registerID: string, payload: UpdatePoSRegister) => {
    try {
      const response = await APIList.updatePoSRegister(registerID, payload);
      if (response.status === 204) {
        return response;
      }
      return null;
    } catch (error) {
      console.debug(error);
      return null;
    }
  };

  const deletePoSRegister = async (registerID: string) => {
    try {
      const response = await APIList.deletePoSRegister(registerID);
      if (response.status === 204) {
        return response;
      }
      return null;
    } catch (error) {
      console.debug(error);
      return null;
    }
  };

  const getRegisterByID = async (registerID: string) => {
    try {
      const params = { include: 'status,extOutlet' };
      const response = await APIList.getPoSRegister(registerID, params);
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.debug(error);
      return null;
    }
  };

  // Once the integration is connected with the pos system, we need to fetch the account to get the available packages to enable or disable pos.
  const fetchAccount = async () => {
    const accountPackages = await accountStore.fetchAccountWithCustomParam({
      include: 'packages',
    });

    accountStore.account.packages = accountPackages.packages;
  };

  return {
    // variables
    availablePoSRegisters,
    posIntegrations,
    posRegister,
    posRegisters,
    sitesWithCameras,
    camerasWithLocationSummary,
    // methods
    isPointOfSalePackageEnabled,
    addPosRegister,
    connectToPoSTerminal,
    deletePoSRegister,
    fetchPoSIntegrations,
    fetchAccount,
    getPoSIntegrationByID,
    getAvailablePoSIntegrationRegisters,
    getPoSRegisters,
    getRegisterByID,
    updatePosRegister,
    updatePoSIntegration,
  };
});
